import { Graphics } from 'pixi.js';
import { Boss } from './boss-base';
import { GAME } from '@/config/game';
import { getTexture } from '@/assets/resolver';
import { BOSS_FEEDBACK } from '@/config/boss-feedback';
import type { BulletSystem } from '@/entities/bullets';
import type { TelegraphSink } from '@/render/telegraphs';
import { PY, PYRE } from './pyre-parts';
import { lerpColor } from './boss-look';

export { PYRE } from './pyre-parts';

type PyreState = 'burning' | 'mantle' | 'rise' | 'air' | 'fall' | 'vent';

/**
 * B6 — Pyre Core (fire Elemental): heat → flame mantle → meteor dive →
 * vent. Def + tuning in pyre-parts.ts, docs/02 Elementals.
 */
export class PyreBoss extends Boss {
  private state: PyreState = 'burning';
  private timer = 0;
  private heat = 0;
  private landX = 0;
  private landY = 0;
  private trailTimer: number = PY.trailInterval;
  private wallTimer = PY.wallInterval * 0.6;
  private lastX: number;
  private lastY: number;
  private time = 0;
  private readonly fx = new Graphics();

  constructor(level: number, x: number, y: number, homeX: number, homeY: number) {
    super(PYRE, level, x, y, homeX, homeY);
    this.lastX = x;
    this.lastY = y;
    this.view.addChildAt(this.fx, 0); // aura + scorch mark behind the body
  }

  /** Mantle and the leap swallow every hit; burning and venting take them. */
  override get isVulnerable(): boolean {
    return super.isVulnerable && (this.state === 'burning' || this.state === 'vent');
  }

  private get airborne(): boolean {
    return this.state === 'air';
  }

  /** Burning or venting: it drifts, drips its wake and fires its patterns. */
  private get roaming(): boolean {
    return this.state === 'burning' || this.state === 'vent';
  }

  /** Nothing fires out of an empty sky — patterns pause during the leap. */
  protected override fireRunners(
    dt: number, px: number, py: number, bullets: BulletSystem, telegraphs?: TelegraphSink,
  ): void {
    if (this.state === 'rise' || this.state === 'air') return;
    super.fireRunners(dt, px, py, bullets, telegraphs);
  }

  override update(
    dt: number, px: number, py: number, bullets: BulletSystem, telegraphs?: TelegraphSink,
  ): void {
    // Planted for the mantle and the leap: no sidestep jink either
    const planted = !this.roaming;
    const holdX = this.x;
    const holdY = this.y;
    super.update(dt, px, py, bullets, telegraphs);
    if (planted) {
      this.x = holdX;
      this.y = holdY;
    }
    if (this.dead) return;
    this.time += dt;
    this.timer -= dt;
    this.cycle(dt, px, py, bullets);
    if (this.roaming) this.wake(dt, bullets);
    this.lastX = this.x;
    this.lastY = this.y;
    this.flameWall(dt, px, bullets);
  }

  /** Heat cycle + meteor dive state machine. */
  private cycle(dt: number, px: number, py: number, bullets: BulletSystem): void {
    switch (this.state) {
      case 'burning':
        this.heat += PY.heatPassive * this.intensity * dt;
        if (this.heat >= 1) this.enter('mantle', PY.mantleTime);
        break;
      case 'mantle':
        if (this.timer <= 0) this.enter('rise', PY.riseTime);
        break;
      case 'rise':
        if (this.timer <= 0) {
          // Lock the strike point where the ship is NOW — the scorch mark shows it
          const inset = GAME.arena.wallThickness + PY.landInset;
          this.landX = Math.max(inset, Math.min(this.arenaW - inset, px));
          this.landY = Math.max(inset, Math.min(this.arenaH - inset, py));
          this.enter('air', PY.airTime);
        }
        break;
      case 'air':
        if (this.timer <= 0) {
          this.x = this.prevX = this.lastX = this.landX;
          this.y = this.prevY = this.lastY = this.landY;
          this.enter('fall', PY.fallTime);
        }
        break;
      case 'fall':
        if (this.timer <= 0) {
          this.impact(bullets);
          this.heat = 0;
          this.enter('vent', PY.ventTime);
        }
        break;
      case 'vent':
        if (this.timer <= 0) this.enter('burning', 0);
        break;
    }
  }

  private enter(state: PyreState, time: number): void {
    this.state = state;
    this.timer = time;
  }

  /** Meteor impact: a fire ring + a crater of burning patches. */
  private impact(bullets: BulletSystem): void {
    const speed = PY.impactSpeed * this.bulletSpeedMul;
    for (let i = 0; i < PY.impactCount; i++) {
      const a = (Math.PI * 2 * i) / PY.impactCount;
      this.spawnFire(bullets, this.x, this.y, a, speed, 4, 11, 1);
    }
    for (let i = 0; i < PY.craterCount; i++) {
      const a = (Math.PI * 2 * i) / PY.craterCount + Math.PI / PY.craterCount;
      this.spawnFire(
        bullets, this.x + Math.cos(a) * PY.craterRadius, this.y + Math.sin(a) * PY.craterRadius,
        a, PY.trailSpeed, PY.craterLife, PY.trailRadius, 1,
      );
    }
    this.events?.burst(this.x, this.y, BOSS_FEEDBACK.pyreImpact);
  }

  /** Burning wake (signature): the floor it crosses keeps burning. */
  private wake(dt: number, bullets: BulletSystem): void {
    const moved = Math.hypot(this.x - this.lastX, this.y - this.lastY);
    this.trailTimer -= dt;
    if (this.trailTimer <= 0 && moved > PY.trailMinSpeed * dt) {
      this.trailTimer = PY.trailInterval;
      const drift = Math.atan2(this.y - this.lastY, this.x - this.lastX) + Math.PI;
      this.spawnFire(bullets, this.x, this.y, drift, PY.trailSpeed, PY.trailLife, PY.trailRadius, 1);
    }
  }

  /** Enrage: a flame wall sweeps the REAL arena toward the ship's side (X3). */
  private flameWall(dt: number, px: number, bullets: BulletSystem): void {
    if (this.hpPct > PY.wallHpPct) return;
    this.wallTimer -= dt * this.intensity;
    if (this.wallTimer > 0) return;
    this.wallTimer = PY.wallInterval;
    const inset = GAME.arena.wallThickness + PY.wallInset;
    const fromLeft = px > this.arenaW / 2;
    const startX = fromLeft ? inset : this.arenaW - inset;
    const life = (this.arenaW - inset * 2) / PY.wallSpeed;
    for (let y = inset; y <= this.arenaH - inset; y += PY.wallGap) {
      this.spawnFire(bullets, startX, y, fromLeft ? 0 : Math.PI, PY.wallSpeed, life, 13, 2);
    }
  }

  private spawnFire(
    bullets: BulletSystem, x: number, y: number, a: number, speed: number,
    life: number, radius: number, damage: number,
  ): void {
    bullets.spawn({
      x, y, dirX: Math.cos(a), dirY: Math.sin(a), speed, life, radius, damage,
      friendly: false, texKey: 'bullet/enemy/fire', rotates: false,
    });
  }

  /** Drifts while burning/venting; plants itself for the mantle and the leap. */
  protected override move(dt: number, px: number, py: number): void {
    if (this.roaming) super.move(dt, px, py);
  }

  override takeDamage(amount: number): boolean {
    if (!this.isVulnerable) {
      this.hitFlash = 0.08; // the mantle eats it
      return false;
    }
    const before = this.hp;
    const dead = super.takeDamage(this.state === 'vent' ? amount * PY.ventMul : amount);
    if (this.state === 'burning') this.heat += (before - this.hp) / (this.maxHp * PY.heatPerHp);
    return dead;
  }

  /** Airborne: shots and blasts sail under it. */
  override absorbBullets(bullets: BulletSystem): { hits: number; dealt: number } {
    if (this.airborne) return { hits: 0, dealt: 0 };
    return super.absorbBullets(bullets);
  }

  override hitByAoe(x: number, y: number, radius: number): boolean {
    return !this.airborne && super.hitByAoe(x, y, radius);
  }

  override touchesPlayer(px: number, py: number, playerRadius: number): boolean {
    if (this.state === 'rise' || this.state === 'air') return false;
    if (this.state === 'fall' && this.timer > PY.fallTime * PY.fallContact) return false;
    return super.touchesPlayer(px, py, playerRadius);
  }

  override render(alpha: number): void {
    super.render(alpha);
    const s = this.state;
    const mantle = s === 'mantle' || s === 'rise';
    this.body.texture = getTexture('boss/pyre/body', mantle || s === 'fall' ? 1 : 0);
    // Leap: rises toward the camera and fades, then drops back in big
    let scale = 1;
    let a = 1;
    if (s === 'rise') {
      const p = 1 - this.timer / PY.riseTime;
      scale = 1 + 0.6 * p;
      a = 1 - 0.8 * p;
    } else if (s === 'air') {
      a = 0;
    } else if (s === 'fall') {
      const p = 1 - this.timer / PY.fallTime;
      scale = 1.8 - 0.8 * p;
      a = 0.3 + 0.7 * p;
    }
    this.body.scale.set(this.body.scale.x * scale);
    this.body.alpha = a;
    // Heat reads from the body: ember red → white-hot; venting = charcoal
    if (s === 'vent') this.body.tint = PY.ventTint;
    else if (s === 'burning' && this.hitFlash <= 0) {
      this.body.tint = lerpColor(this.body.tint, PY.hotTint, Math.min(1, this.heat) * 0.75);
    }
    this.drawFx(alpha);
  }

  private drawFx(alpha: number): void {
    const g = this.fx;
    g.clear();
    const r = this.def.radius;
    const s = this.state;
    if (s === 'burning' && this.heat > 0.3) {
      // The aura thickens as heat builds — the overheat is never a surprise
      g.circle(0, 0, r * 1.3).fill({ color: PY.mantleColor, alpha: (this.heat - 0.3) * 0.3 });
    } else if (s === 'mantle' || s === 'rise') {
      const pulse = 1 + Math.sin(this.time * 18) * 0.06;
      g.circle(0, 0, r * 1.55 * pulse).fill({ color: PY.mantleColor, alpha: 0.4 });
      g.circle(0, 0, r * 1.2 * pulse).stroke({ color: PY.mantleCore, width: 4, alpha: 0.8 });
    } else if (s === 'vent') {
      // Steam puffs rise off the open vents
      for (let i = 0; i < 3; i++) {
        const t = (this.time * 0.9 + i / 3) % 1;
        g.circle((i - 1) * r * 0.5, -r * (0.4 + t), 8 + 10 * t)
          .fill({ color: PY.steamColor, alpha: 0.4 * (1 - t) });
      }
    }
    if (s === 'air' || s === 'fall') {
      // Glowing scorch mark at the strike point (world → view space)
      const vx = this.prevX + (this.x - this.prevX) * alpha;
      const vy = this.prevY + (this.y - this.prevY) * alpha;
      const total = PY.airTime + PY.fallTime;
      const left = s === 'air' ? this.timer + PY.fallTime : this.timer;
      const p = 1 - left / total;
      const lx = this.landX - vx;
      const ly = this.landY - vy;
      g.circle(lx, ly, PY.markRadius * (0.4 + 0.6 * p)).fill({ color: PY.markShadow, alpha: 0.35 });
      g.circle(lx, ly, PY.markRadius * (0.4 + 0.6 * p))
        .stroke({ color: PY.markColor, width: 3 + 3 * p, alpha: 0.5 + 0.4 * p });
    }
  }
}
